import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowRight, ChevronRight } from "lucide-react";
import type { BaselineDeliverable, BaselineEpic, BaselineStory } from "@/hooks/use-baseline";
import { cn, titleCase } from "@/lib/utils";

interface ScopeTilesProps {
  deliverables: BaselineDeliverable[];
  drillD: string | null;
  drillE: string | null;
}

export function ScopeTiles({ deliverables, drillD, drillE }: ScopeTilesProps) {
  const { solutionId } = useParams({ from: "/s/$solutionId" });
  const navigate = useNavigate();

  const drilledDeliverable = drillD ? (deliverables.find((d) => d.id === drillD) ?? null) : null;
  const drilledEpic =
    drilledDeliverable && drillE
      ? (drilledDeliverable.epics.find((e) => e.id === drillE) ?? null)
      : null;

  function setDrill(d: string | null, e: string | null) {
    navigate({
      to: "/s/$solutionId/scope",
      params: { solutionId },
      search: (prev) => ({ ...prev, d: d ?? undefined, e: e ?? undefined }),
    });
  }

  return (
    <div className="space-y-4">
      <Breadcrumb
        deliverable={drilledDeliverable}
        epic={drilledEpic}
        onPickRoot={() => setDrill(null, null)}
        onPickDeliverable={(id) => setDrill(id, null)}
      />

      {!drilledDeliverable && (
        <DeliverableTiles deliverables={deliverables} onPick={(id) => setDrill(id, null)} />
      )}

      {drilledDeliverable && !drilledEpic && (
        <EpicTiles
          deliverable={drilledDeliverable}
          onPick={(id) => setDrill(drilledDeliverable.id, id)}
        />
      )}

      {drilledDeliverable && drilledEpic && (
        <StoryTiles deliverable={drilledDeliverable} epic={drilledEpic} solutionId={solutionId} />
      )}
    </div>
  );
}

// ── Breadcrumb ────────────────────────────────────────────────────

function Breadcrumb({
  deliverable,
  epic,
  onPickRoot,
  onPickDeliverable,
}: {
  deliverable: BaselineDeliverable | null;
  epic: BaselineEpic | null;
  onPickRoot: () => void;
  onPickDeliverable: (id: string) => void;
}) {
  return (
    <nav aria-label="Breadcrumb" className="text-sm">
      <ol className="flex items-center gap-2 flex-wrap text-muted-foreground">
        <li>
          {deliverable ? (
            <button
              type="button"
              onClick={onPickRoot}
              className="hover:text-foreground hover:underline"
            >
              All deliverables
            </button>
          ) : (
            <span className="font-display font-semibold text-foreground">All deliverables</span>
          )}
        </li>
        {deliverable && (
          <>
            <li aria-hidden>
              <ChevronRight className="h-3.5 w-3.5" />
            </li>
            <li>
              {epic ? (
                <button
                  type="button"
                  onClick={() => onPickDeliverable(deliverable.id)}
                  className="hover:text-foreground hover:underline inline-flex items-center gap-1.5"
                >
                  <code className="font-mono text-xs">{deliverable.code}</code>
                  {deliverable.title}
                </button>
              ) : (
                <span className="font-display font-semibold text-foreground inline-flex items-center gap-1.5">
                  <code className="font-mono text-xs text-muted-foreground">
                    {deliverable.code}
                  </code>
                  {deliverable.title}
                </span>
              )}
            </li>
          </>
        )}
        {epic && (
          <>
            <li aria-hidden>
              <ChevronRight className="h-3.5 w-3.5" />
            </li>
            <li>
              <span className="font-display font-semibold text-foreground inline-flex items-center gap-1.5">
                <code className="font-mono text-xs text-muted-foreground">{epic.code}</code>
                {epic.title}
              </span>
            </li>
          </>
        )}
      </ol>
    </nav>
  );
}

// ── L1 tiles ──────────────────────────────────────────────────────

function DeliverableTiles({
  deliverables,
  onPick,
}: {
  deliverables: BaselineDeliverable[];
  onPick: (id: string) => void;
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
      {deliverables.map((d) => {
        const epicCount = d.epics.length;
        const storyCount = d.epics.reduce((n, e) => n + e.stories.length, 0);
        return (
          <button
            key={d.id}
            type="button"
            onClick={() => onPick(d.id)}
            className={cn(
              "group rounded-xl border border-[var(--color-border)] bg-card p-5 text-left transition-all",
              "hover:-translate-y-0.5 hover:border-nymbl-blue hover:shadow-sm",
              "flex flex-col gap-3 min-h-[180px]",
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <code className="font-mono text-[11px] tabular-nums text-muted-foreground">
                {d.code}
              </code>
              <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                {titleCase(d.group)}
              </span>
            </div>
            <h3 className="font-display text-lg font-semibold leading-snug text-foreground">
              {d.title}
            </h3>
            {d.summary && <p className="text-sm text-foreground/70 line-clamp-3">{d.summary}</p>}
            <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground">
              <span className="tabular-nums">
                {epicCount} epic{epicCount === 1 ? "" : "s"} · {storyCount} stor
                {storyCount === 1 ? "y" : "ies"}
              </span>
              <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-nymbl-blue" />
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ── L2 tiles ──────────────────────────────────────────────────────

function EpicTiles({
  deliverable,
  onPick,
}: {
  deliverable: BaselineDeliverable;
  onPick: (id: string) => void;
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
      {deliverable.epics.map((e) => (
        <button
          key={e.id}
          type="button"
          onClick={() => onPick(e.id)}
          className={cn(
            "group rounded-xl border border-[var(--color-border)] bg-card p-5 text-left transition-all",
            "hover:-translate-y-0.5 hover:border-nymbl-blue hover:shadow-sm",
            "flex flex-col gap-3 min-h-[140px]",
          )}
        >
          <code className="font-mono text-[11px] tabular-nums text-muted-foreground">{e.code}</code>
          <h3 className="font-display text-base font-semibold leading-snug text-foreground">
            {e.title}
          </h3>
          <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground">
            <span className="tabular-nums">
              {e.stories.length} stor{e.stories.length === 1 ? "y" : "ies"}
            </span>
            <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-nymbl-blue" />
          </div>
        </button>
      ))}
    </div>
  );
}

// ── L3 tiles ──────────────────────────────────────────────────────

function StoryTiles({
  epic,
  solutionId,
}: {
  deliverable: BaselineDeliverable;
  epic: BaselineEpic;
  solutionId: string;
}) {
  if (epic.stories.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--color-border)] p-10 text-center">
        <p className="text-sm text-muted-foreground">No stories under this epic yet.</p>
      </div>
    );
  }
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {epic.stories.map((s) => (
        <StoryTile key={s.id} story={s} solutionId={solutionId} />
      ))}
    </div>
  );
}

function StoryTile({ story, solutionId }: { story: BaselineStory; solutionId: string }) {
  const primary = story.label?.trim() || story.text;
  return (
    <Link
      to="/s/$solutionId/scope/$storyId"
      params={{ solutionId, storyId: story.id }}
      className={cn(
        "group rounded-xl border border-[var(--color-border)] bg-card p-4 transition-all",
        "hover:-translate-y-0.5 hover:border-nymbl-blue hover:shadow-sm",
        "flex flex-col gap-2",
      )}
    >
      <div className="flex items-center justify-between">
        <code className="font-mono text-[11px] tabular-nums text-muted-foreground">
          {story.code}
        </code>
        {story.acceptanceCriteria.length > 0 && (
          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            {story.acceptanceCriteria.length} AC
          </span>
        )}
      </div>
      <p className="text-sm font-medium text-foreground line-clamp-3">{primary}</p>
      <div className="mt-auto text-xs text-muted-foreground inline-flex items-center justify-end gap-1 group-hover:text-nymbl-blue">
        Open
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}
